import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useOnboardingStore } from '@/hooks/use-onboarding-store';
import OnboardingHeader from '@/components/onboarding/OnboardingHeader';
import OnboardingFooter from '@/components/onboarding/OnboardingFooter';

// Import all step components
import Step1Landing from '@/components/onboarding/steps/Step1Landing';
import Step2Goal from '@/components/onboarding/steps/Step2Goal';
import Step3Experience from '@/components/onboarding/steps/Step3Experience';
import Step4Challenge from '@/components/onboarding/steps/Step4Challenge';
import Step5Solution from '@/components/onboarding/steps/Step5Solution';
import Step6Outcome from '@/components/onboarding/steps/Step6Outcome';
import Step7AIFamiliarity from '@/components/onboarding/steps/Step7AIFamiliarity';
import Step7Loading from '@/components/onboarding/steps/Step7Loading';
import Step8Email from '@/components/onboarding/steps/Step8Email';
import Step9Conversion from '@/components/onboarding/steps/Step9Conversion';
import Step10Payment from '@/components/onboarding/steps/Step10Payment';
import Step12ThankYou from '@/components/onboarding/steps/Step12ThankYou';

const OnboardingFlow: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, nextStep, prevStep, email } = useOnboardingStore();

  const handleNext = () => {
    console.log('handleNext called, currentStep:', currentStep);
    
    // Check if step 11 (payment) is being accessed without email
    if (currentStep === 10 && !email) {
      console.log('Email required before proceeding to payment step');
      return;
    }
    
    if (currentStep === 12) {
      // Thank you step completed, navigate to main course area or stay on thank you
      console.log('Onboarding completed');
      // Could redirect to course dashboard or stay on thank you page
    } else {
      console.log('Calling nextStep to go from step', currentStep, 'to step', currentStep + 1);
      nextStep();
    }
  };

  const handleBack = () => {
    if (currentStep === 1) {
      navigate('/nocode');
    } else {
      prevStep();
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1Landing onNext={handleNext} />;
      case 2:
        return <Step2Goal onNext={handleNext} />;
      case 3:
        return <Step3Experience onNext={handleNext} />;
      case 4:
        return <Step4Challenge onNext={handleNext} />;
      case 5:
        return <Step5Solution onNext={handleNext} />;
      case 6:
        return <Step6Outcome onNext={handleNext} />;
      case 7:
        return <Step7AIFamiliarity onNext={handleNext} />;
      case 8:
        return <Step7Loading onNext={handleNext} />;
      case 9:
        return <Step8Email onNext={handleNext} />;
      case 10:
        // Only show conversion step if email is collected
        if (!email) {
          console.log('Email not collected, redirecting to email step');
          return <Step8Email onNext={handleNext} />;
        }
        return <Step9Conversion onNext={handleNext} />;
      case 11:
        return <Step10Payment onNext={handleNext} />;
      case 12:
        return <Step12ThankYou onNext={handleNext} />;
      default:
        return <Step1Landing onNext={handleNext} />;
    }
  };

  // Add the onboarding-page class to document body for styling
  React.useEffect(() => {
    document.body.classList.add('onboarding-page-body');
    document.documentElement.classList.add('onboarding-page-html');
    
    return () => {
      document.body.classList.remove('onboarding-page-body');
      document.documentElement.classList.remove('onboarding-page-html');
    };
  }, []);

  // Step 1, Step 10, Step 11, and Step 12 are full screen, others have header/footer
  if (currentStep === 1 || currentStep === 10 || currentStep === 11 || currentStep === 12) {
    return (
      <div className="onboarding-page min-h-screen bg-white overflow-hidden" style={{ backgroundColor: 'white' }}>
        <AnimatePresence mode="wait">
          <motion.div key={currentStep}>
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="onboarding-page min-h-screen bg-white flex flex-col" style={{ backgroundColor: 'white' }}>
      <OnboardingHeader
        currentStep={currentStep}
        totalSteps={12}
        onBack={handleBack}
        showBack={currentStep > 1}
      />
      
      <div className="flex-1 flex items-center justify-center py-8 bg-white" style={{ backgroundColor: 'white' }}>
        <AnimatePresence mode="wait">
          <motion.div key={currentStep} className="w-full max-w-2xl">
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="px-6 bg-white" style={{ backgroundColor: 'white' }}>
        <OnboardingFooter />
      </div>
    </div>
  );
};

export default OnboardingFlow;